import { batch } from 'react-redux';
import { isEmpty } from 'lodash';
import { checkVanityUrlAvailability } from '@client/listeners/utils/router';
import { showError } from '@client/listeners/lib/alerts';
import { getSessionUser } from '@client/selectors/helpers/session';
import {
  upsertAct, startCreateAct, startCreatePartialAct, updateAct,
  startPatchAct,
} from '@client/slices/entities/acts.reducer';
import { getLocationDetails } from '@client/listeners/lib/requests';
import { trackAnalytics } from '@client/intercom';
import { setLoaderIsLoading, setLoaderIsFinishedSuccess, setLoaderIsFinishedError } from '@client/slices/helpers/loaders.reducer';
import { NotificationTypes, ProfileTypes } from '@sda/enums';
import {
  getSanitizedVanityUrl, addToVanityUrlCacheArray,
} from '@sda/utils/url';
import { updateCurrentSessionUser } from '@client/slices/helpers/session.reducer';
import { enrichUser, getVanityUrl } from '@client/listeners/utils/session';
import { AnalyticsEvents } from '@sda/interfaces/queue/analytics';
import { getSelectedProfile } from '@client/selectors/helpers/profile';
import { getEmbedData } from './utils';
import type { AddListener } from '@client/listeners/lib';
import type { Writable } from 'type-fest';
import type { Act } from '@sda/interfaces/act';

export const artistFormListeners = (addListener: AddListener) => [
  addListener({
    actionCreator: startPatchAct,
    effect: async (action, listenerApi) => {
      const profile = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;
      const firebase = window.Firebase;

      const scope = 'signup-act-create-form';
      dispatch(setLoaderIsLoading(scope));

      try {
        const {
          id, bioHTML, profileType, publicImageUrl,
        } = getSelectedProfile(state);

        const actData = {
          ...profile,
          ...await getLocationDetails(profile) || {},
          ...await getEmbedData({ ...profile, audioUri: profile.spotifyArtistUrl }),
          ...await getVanityUrl(listenerApi, profile.name),
        };

        const publicAct = firebase.SDK.Acts.filter(actData, firebase.SDK.Acts.PublicMap, true);

        await firebase.SDK.Acts.propagate(id, publicAct);

        batch(() => {
          dispatch(setLoaderIsFinishedSuccess(scope));
          dispatch(updateAct({ id, changes: publicAct }));
        });

        trackAnalytics(startPatchAct, { ...actData });

        // Mark this profile as completed if either of these fields are empty in the previous form state
        if (isEmpty(bioHTML) || isEmpty(publicImageUrl)) {
          trackAnalytics(AnalyticsEvents.COMPLETED_PROFILE, {
            profileType,
          });
        }
      } catch (e) {
        showError(e);
        dispatch(setLoaderIsFinishedError(scope, e));
      }
    },
  }),

  addListener({
    actionCreator: startCreateAct,
    effect: async (action, listenerApi) => {
      const act = action.payload;
      const firebase = window.Firebase;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      const user = getSessionUser(state);

      const scope = 'signup-act-create-form';
      dispatch(setLoaderIsLoading(scope));

      try {
        const actId = firebase.SDK.Acts.getNextId();

        let vanityUrl = getSanitizedVanityUrl(act.name);

        // get available vanityUrl
        vanityUrl = await checkVanityUrlAvailability({ objectType: ProfileTypes.ACT, vanityUrl });
        const vanityUrlLowercase = vanityUrl.toLowerCase();

        let actData: Writable<Partial<Act>> = {
          ...act,
          ...await getLocationDetails(act) || {},
          vanityUrl,
          vanityUrlLowercase,
          vanityUrlCache: addToVanityUrlCacheArray([], vanityUrlLowercase),
          fullName: `${user.firstName} ${user.lastName}`,
          primaryEmail: user.email,
          isRejected: false,
          owner: user.id,
          collaborators: [
            user.id,
          ],
        };

        actData = {
          ...actData,
          ...await getEmbedData(act),
        };

        const userData = {
          selectedProfileId: actId,
          selectedProfileType: ProfileTypes.ACT,
        };

        const publicAct = firebase.SDK.Acts.filter(actData, firebase.SDK.Acts.PublicMap);
        const privateAct = firebase.SDK.Acts.Private().filter(actData, firebase.SDK.Acts.PrivateMap);

        await firebase.helpers.runTransaction(async () => {
          await firebase.SDK.Acts.set(actId, publicAct);
          await firebase.SDK.Acts.Private().set(actId, privateAct);

          await firebase.SDK.Users.Private().update(user.id, userData);

          await firebase.SDK.Notifications.addNotification(
            user.id,
            NotificationTypes.NOTIFICATION_TYPE_ACCOUNT_UPDATE,
            {
              profileType: 'Artist',
              profileName: actData.name,
              template: 'ACCOUNT_CREATE_PROFILE',
            },
          );
        });

        batch(() => {
          dispatch(upsertAct({ id: actId, ...privateAct, ...publicAct }));
          dispatch(updateCurrentSessionUser(userData));
          dispatch(setLoaderIsFinishedSuccess(scope));
        });

        listenerApi.fork(async () => {
          await enrichUser(listenerApi);
        });

        trackAnalytics(startCreateAct, {
          isPartialProfile: startCreatePartialAct.match(action),
          profileType: 'Artist',
          ...actData,
        });
      } catch (e) {
        showError(e);
        dispatch(setLoaderIsFinishedError(scope, e));
      }
    },
  }),
];
